import { observer } from "mobx-react-lite";
import { useState } from "react";
import { toastModule } from "@/modules/toast";
import { api } from "@/modules/api";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import styled from "@emotion/styled";
import {
  MDSIconButton,
  MDSRowItemSlotName,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";

interface SearchResultVotingButtonsProps {
  itemId: string;
  searchEngineResultId: string;
  pageCursor: string | null;
  searchSessionId: string;
  userSpaceId: string;
  additionalElements?: React.ReactNode;
  adminScore?: number;
  slot?: MDSRowItemSlotName;
}

enum SearchResultVote {
  None,
  Upvote,
  Downvote,
}

export const SearchResultVotingButtons = observer<SearchResultVotingButtonsProps>(
  function SearchResultVotingButtons({
    itemId,
    searchEngineResultId,
    pageCursor,
    searchSessionId,
    userSpaceId,
    additionalElements,
    adminScore,
  }) {
    const [vote, setVote] = useState(SearchResultVote.None);
    const searchPage = useTabPageStore("searchPage");
    const searchPageListState = searchPage.listState;
    const highlightedItemId = searchPageListState?.highlightedItemId;
    const isVisible = itemId === highlightedItemId;

    async function submitFeedback(
      voteType: SearchResultVote,
      searchEngineResultId: string,
      searchSessionID: string,
      pageCursor: string | null
    ) {
      try {
        const response = await api.post("/v2/search/feedback", {
          params: {
            query: {
              space_id: userSpaceId,
            },
          },
          body: {
            search_engine_result_id: searchEngineResultId,
            search_session_page_cursor: pageCursor,
            session_id: searchSessionID,
            feedback_kind: voteType === SearchResultVote.Upvote ? "UPVOTE" : "DOWNVOTE",
          },
        });
        if (response.error) {
          toastModule.triggerToast({
            content: `Failed to submit Feedback for Note with SearchEngineResultID ${searchEngineResultId}`,
          });
          return null;
        } else {
          toastModule.triggerToast({ content: "Thanks for the feedback" });
        }
      } catch (_error) {
        toastModule.triggerToast({
          content: `An error occurred while submitting the response for Note with SearchEngineResult ID  ${searchEngineResultId}`,
        });
        return null;
      }
    }

    const handleSearchResultVote = async (
      event: React.MouseEvent,
      voteType: SearchResultVote,
      searchEngineResultId: string,
      searchSessionId: string,
      pageCursor: string | null
    ) => {
      event.preventDefault();
      event.stopPropagation();
      setVote(voteType);
      await submitFeedback(voteType, searchEngineResultId, searchSessionId, pageCursor);
    };

    const handleSearchResultVoteWithArgs =
      (
        voteType: SearchResultVote,
        result_id: string,
        searchSessionID: string,
        pageCursor: string | null
      ) =>
      (event: React.MouseEvent) => {
        handleSearchResultVote(event, voteType, result_id, searchSessionID, pageCursor);
      };

    const buttonsDisabled = vote !== SearchResultVote.None;
    if (!isVisible) return null;

    return (
      <Container>
        {adminScore !== undefined && (
          <StyledScore variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
            {adminScore >= 100
              ? adminScore.toFixed(0)
              : adminScore >= 10
                ? adminScore.toFixed(1)
                : adminScore.toFixed(3)}
          </StyledScore>
        )}
        <MDSIconButton
          iconKind={MDSIconKind.Upvote}
          isDisabled={buttonsDisabled}
          onClick={handleSearchResultVoteWithArgs(
            SearchResultVote.Upvote,
            searchEngineResultId,
            searchSessionId,
            pageCursor
          )}
        />
        <MDSIconButton
          iconKind={MDSIconKind.Downvote}
          isDisabled={buttonsDisabled}
          onClick={handleSearchResultVoteWithArgs(
            SearchResultVote.Downvote,
            searchEngineResultId,
            searchSessionId,
            pageCursor
          )}
        />

        {additionalElements ? (
          <AdditionalElementsContainer>{additionalElements}</AdditionalElementsContainer>
        ) : null}
      </Container>
    );
  }
);

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-self: center;
`;

const AdditionalElementsContainer = styled.div`
  display: inline-block;
  margin-left: ${({ theme }) => theme.spacing["2"]};
  height: 30px;
`;

const StyledScore = styled(MDSText)`
  align-self: center;
  margin-right: ${({ theme }) => theme.spacing[2]};
`;
